import React from "react";
import clsx from "clsx";
import { Text } from "@merge-api/merge-javascript-shared";
import { Field, ScopeOption } from "../../../../../../models/CommonModel";
import { fieldTypeLabel } from "../constants";
import { scopeForField } from "../utils/scopeMapping";
import ScopePermissionSwitch from "./ScopePermissionSwitch";

interface Props {
  field: Field;
  isViewing: boolean;
  areOptionalScopesEnabled: boolean;
  disabled: boolean;
  /** Explanation shown on hover when the scope switch is disabled (may include a link). */
  disabledTooltip?: React.ReactNode;
  onChange: (scope: ScopeOption) => void;
  /** True when this field expands into a sub-object; flips the type label from "uuid" to "object". */
  isSubObject?: boolean;
  /** When set, replaces the field description with the deprecation message (gray, code in monospace). */
  deprecationMessage?: React.ReactNode;
  /** Past-dated deprecation: dims the text columns to 60%; the switch stays full opacity + usable. */
  isPastDeprecation?: boolean;
  /** Optional note rendered directly beneath the row (e.g. a related-object relation note). */
  note?: React.ReactNode;
  /** Indents the row for fields nested under a sub-object. */
  nested?: boolean;
}

/** A field row under an object: field name (monospace), type, description, scope control. */
const ScopeFieldRow = ({
  field,
  isViewing,
  areOptionalScopesEnabled,
  disabled,
  disabledTooltip,
  onChange,
  isSubObject = false,
  deprecationMessage,
  isPastDeprecation = false,
  note,
  nested = false,
}: Props) => {
  // derived state
  const scope = scopeForField(field, areOptionalScopesEnabled);

  // render
  return (
    // Baseline-align the columns: the name/type (sm-mono, 11px) and description (sm, 12px) differ in
    // font, so aligning box tops leaves glyphs slightly off; baseline alignment lines up their text
    // instead. For a wrapped description the first line's baseline is used, so the name / type /
    // switch stay pinned to the first line. A past-dated deprecation dims the text columns to 60%
    // (the "deprecated" cue) but leaves the switch full opacity + clickable.
    <div className="flex items-baseline gap-8 px-8 py-3">
      {/* Sub-fields indent via 16px of left padding inside the fixed-width name column, so the
          indent doesn't shift the type / description / switch columns relative to top-level fields. */}
      <div
        className={clsx(
          "flex w-40 shrink-0 items-center",
          nested && "pl-4",
          isPastDeprecation && "opacity-60",
        )}
      >
        <Text variant="sm-mono" className="min-w-0 truncate text-gray-60">
          {field.field_name}
        </Text>
      </div>
      <div className={clsx("flex w-16 shrink-0 items-center", isPastDeprecation && "opacity-60")}>
        <Text variant="sm-mono" className="text-gray-50">
          {fieldTypeLabel(field, isSubObject)}
        </Text>
      </div>
      {/* The relation note reads as a lead-in before the field's own description; the full text wraps
          onto as many lines as it needs (a deprecation message replaces the description when present).
          No tooltip on the description itself; the link inside the note keeps its own tooltip. */}
      <div className={clsx("min-w-0 flex-1", isPastDeprecation && "opacity-60")}>
        {deprecationMessage ? (
          <Text variant="sm" className="text-gray-50">
            {note}
            {note && " "}
            {deprecationMessage}
          </Text>
        ) : (
          (note || field.field_description) && (
            <Text variant="sm" className="text-gray-50">
              {note}
              {note && field.field_description && " "}
              {field.field_description}
            </Text>
          )
        )}
      </div>
      <ScopePermissionSwitch
        scope={scope}
        isViewing={isViewing}
        areOptionalScopesEnabled={areOptionalScopesEnabled}
        disabled={disabled}
        disabledTooltip={disabledTooltip}
        onChange={onChange}
      />
    </div>
  );
};

export default ScopeFieldRow;
